import { useTranslation } from 'react-i18next';
import cx from 'clsx';
import React, { useCallback, useEffect } from 'react';
import ReactGA4 from 'react-ga4';
import clsx from 'clsx';
import DropDown from 'antd/es/dropdown';

import {
  IconRcMainLogo,
  IconMiniLogo,
  IconRcHome,
  IconRcHomeActive,
  IconRcSettings,
  IconMore,
  IconRcProfile,
  IconRcProfileActive,
  IconRcL2Wallet,
  IconRcL2WalletActive,
  IconRcNoti,
  IconRcNotiActive,
  IconRcSidebarHi,
  IconRcSidebarHiActive,
  IconRcPaid,
  IconRcMarket,
  IconBundle,
  IconBadgeEntry,
  IconVote,
  IconRcGroup,
  IconOfficialConsole,
  IconRanking,
  IconRcPortfolio,
  IconRcI18n,
  IconRcNotiDarkActive,
  IconRcHomeDarkActive,
  IconRcProfileDarkActive,
  IconRcL2WalletDarkActive,
  IconRcSidebarHiDarkActive,
  IconRcBookmark,
  IconRcBookmarkActive,
  IconRcBookmarkDarkActive,
  IconRcCredit,
  IconRcCreditActive,
  IconRcCreditDarkActive,
  IconRcProtocols,
  IconRcChannel,
  IconRcChannelDarkActive,
  IconRcChannelActive,
  IconRcXP,
  IconRcXPActive,
  IconRcXPDarkActive,
  IconRcQuest,
  IconRcQuestActive,
  IconRcQuestDarkActive,
  IconSybil,
} from '@/assets/sidebar';

import { StatedNavLink } from '@/components';
import {
  DEVICE_TYPE,
  useDeviceType,
  useHiChatLocalUserInfo,
  useWindowVisible,
  useDeviceTypeIsMobile,
  useTheme,
  getGaUserType,
} from '@/hooks';
import { unreadCountFormat } from '@/utils/math';
import { useHiUnreadMessageCount } from '@/views/Chats/hooks';
import { useIsLastFeedUnRead } from '@/views/Stream';
import { useUnreadNotificationCount } from '@/views/Notification';

import { CLEAR_BACK_STATE } from '../../const';
import { AccountSwitcher } from '../AccountSwitcher';
import { StreamOpenEditorButton } from '../StreamOpenEditorButton';
import { I18nSwitch } from '../I18nSwitch';
import style from './index.module.scss';

export const Sidebar = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();

  const deviceType = useDeviceType();
  const isVisible = useWindowVisible();
  const isMobile = useDeviceTypeIsMobile();
  const { hiChatLocalUserInfo } = useHiChatLocalUserInfo();
  useHiUnreadMessageCount(isVisible ? 10000 : undefined);

  const { data: isLastStreamUnRead } = useIsLastFeedUnRead();
  const { data: noticeCount } = useUnreadNotificationCount();

  const handleClickStreamTab = useCallback(() => {
    if (isLastStreamUnRead && !isLastStreamUnRead?.is_read) {
      ReactGA4.event({
        category: 'Stream Notification',
        action: `Enter_Stream_Notification`,
      });
    }
  }, [isLastStreamUnRead]);

  const handleClickMore = useCallback(() => {
    ReactGA4.event({
      category: 'Click More Module',
      action: `More_Click_${getGaUserType()}`,
    });
  }, []);

  const handleClickMoreItem = useCallback((moduleName: string) => {
    ReactGA4.event({
      category: 'Click More Module',
      action: `More_${moduleName}_${getGaUserType()}`,
    });
  }, []);

  const handleClickNewPost = useCallback(() => {
    ReactGA4.event({
      category: 'New Post',
      action: `New Post_Click_${getGaUserType()}`,
    });
  }, []);

  useEffect(() => {
    if (isLastStreamUnRead && !isLastStreamUnRead?.is_read) {
      ReactGA4.event({
        category: 'Stream Notification',
        action: `Has_Stream_Notification`,
      });
    }
  }, [isLastStreamUnRead]);

  if (isMobile) return null;

  const mini = deviceType === DEVICE_TYPE.TABLET || deviceType === DEVICE_TYPE.MOBILE;

  return (
    <nav className={cx(style.container, mini && style.mini)}>
      <div className={style.inner}>
        <div className={style.top}>
          <StatedNavLink to="/" className={style.logoWrap}>
            {mini ? (
              <img src={IconMiniLogo} alt="" />
            ) : (
              <IconRcMainLogo className={style.mainLogo} />
            )}
          </StatedNavLink>
          <div className={style.menuList}>
            <StatedNavLink
              to="/"
              exact
              className={clsx(style.navLink)}
              activeClassName={style.selected}
              onClick={handleClickStreamTab}>
              <div className={style.menu}>
                <IconRcHome className={style.menuIcon} />
                {isDark ? (
                  <IconRcHomeDarkActive className={style.menuIconActive} />
                ) : (
                  <IconRcHomeActive className={style.menuIconActive} />
                )}
                {isLastStreamUnRead && !isLastStreamUnRead?.is_read ? (
                  <div className={style.streamNotification} />
                ) : null}
                <span className={style.menuText}>{t('Sidebar.1cc1r')}</span>
              </div>
            </StatedNavLink>
            <StatedNavLink
              initialState={CLEAR_BACK_STATE}
              to="/quest"
              className={style.navLink}
              activeClassName={style.selected}>
              <div className={style.menu}>
                <IconRcQuest className={style.menuIcon} />
                {isDark ? (
                  <IconRcQuestDarkActive className={style.menuIconActive} />
                ) : (
                  <IconRcQuestActive className={style.menuIconActive} />
                )}
                {!mini ? <span className={style.menuText}>Quest</span> : null}
              </div>
            </StatedNavLink>
            <StatedNavLink
              initialState={CLEAR_BACK_STATE}
              to="/profile"
              className={style.navLink}
              activeClassName={style.selected}>
              <div className={style.menu}>
                <IconRcProfile className={style.menuIcon} />
                {isDark ? (
                  <IconRcProfileDarkActive className={style.menuIconActive} />
                ) : (
                  <IconRcProfileActive className={style.menuIconActive} />
                )}
                {!mini ? (
                  <span className={style.menuText}>{t('OfficialAccountCard.mev895')}</span>
                ) : null}
              </div>
            </StatedNavLink>
            <StatedNavLink
              initialState={CLEAR_BACK_STATE}
              to="/account"
              className={style.navLink}
              activeClassName={style.selected}>
              <div className={style.menu}>
                <IconRcL2Wallet className={style.menuIcon} />
                {isDark ? (
                  <IconRcL2WalletDarkActive className={style.menuIconActive} />
                ) : (
                  <IconRcL2WalletActive className={style.menuIconActive} />
                )}
                <span className={style.menuText}>{t('Sidebar.10x7t3n')}</span>
              </div>
            </StatedNavLink>
            <StatedNavLink
              to="/notification"
              initialState={CLEAR_BACK_STATE}
              className={style.navLink}
              activeClassName={style.selected}>
              <div className={style.menu}>
                <IconRcNoti className={style.menuIcon} />
                {isDark ? (
                  <IconRcNotiDarkActive className={style.menuIconActive} />
                ) : (
                  <IconRcNotiActive className={style.menuIconActive} />
                )}
                {noticeCount ? (
                  <div
                    className={cx(
                      style.hiMessageNotification,
                      hiChatLocalUserInfo?.unreadMessageCount > 99 && style.greaterThan99,
                    )}>
                    {unreadCountFormat(noticeCount)}
                  </div>
                ) : null}
                {!mini ? (
                  <span className={style.menuText}>{t('NotificationModal.ck7v2j')}</span>
                ) : null}
              </div>
            </StatedNavLink>
            <StatedNavLink
              to="/bookmarks"
              initialState={CLEAR_BACK_STATE}
              className={style.navLink}
              activeClassName={style.selected}>
              <div className={style.menu}>
                <IconRcBookmark className={style.menuIcon} />
                {isDark ? (
                  <IconRcBookmarkDarkActive className={style.menuIconActive} />
                ) : (
                  <IconRcBookmarkActive className={style.menuIconActive} />
                )}
                {!mini ? <span className={style.menuText}>{t('StreamTab.1utzz6l')}</span> : null}
              </div>
            </StatedNavLink>
            <StatedNavLink
              to="/hi"
              initialState={CLEAR_BACK_STATE}
              className={style.navLink}
              activeClassName={style.selected}>
              <div className={style.menu}>
                <IconRcSidebarHi className={style.menuIcon} />
                {isDark ? (
                  <IconRcSidebarHiDarkActive className={style.menuIconActive} />
                ) : (
                  <IconRcSidebarHiActive className={style.menuIconActive} />
                )}
                {hiChatLocalUserInfo.unreadMessageCount ? (
                  <div
                    className={cx(
                      style.hiMessageNotification,
                      hiChatLocalUserInfo?.unreadMessageCount > 99 && style.greaterThan99,
                    )}>
                    {unreadCountFormat(hiChatLocalUserInfo.unreadMessageCount)}
                  </div>
                ) : null}
                <span className={style.menuText}>Hi</span>
              </div>
            </StatedNavLink>
            <StatedNavLink
              to="/channels"
              initialState={CLEAR_BACK_STATE}
              className={style.navLink}
              activeClassName={style.selected}>
              <div className={style.menu}>
                <IconRcChannel className={style.menuIcon} />
                {isDark ? (
                  <IconRcChannelDarkActive className={style.menuIconActive} />
                ) : (
                  <IconRcChannelActive className={style.menuIconActive} />
                )}
                <span className={style.menuText}>{t('DetailContainer.13r1kgz')}</span>
              </div>
            </StatedNavLink>
            <StatedNavLink
              to="/credit"
              initialState={CLEAR_BACK_STATE}
              className={style.navLink}
              activeClassName={style.selected}>
              <div className={style.menu}>
                <IconRcCredit className={style.menuIcon} />
                {isDark ? (
                  <IconRcCreditDarkActive className={style.menuIconActive} />
                ) : (
                  <IconRcCreditActive className={style.menuIconActive} />
                )}
                <span className={style.menuText}>{t('CreditHeader.xijuhl')}</span>
              </div>
            </StatedNavLink>
            <StatedNavLink
              to="/xp"
              initialState={CLEAR_BACK_STATE}
              className={style.navLink}
              activeClassName={style.selected}>
              <div className={style.menu}>
                <IconRcXP className={style.menuIcon} />
                {isDark ? (
                  <IconRcXPDarkActive className={style.menuIconActive} />
                ) : (
                  <IconRcXPActive className={style.menuIconActive} />
                )}
                <span className={style.menuText}>XP</span>
              </div>
            </StatedNavLink>
            <DropDown
              trigger={['click']}
              placement="topLeft"
              overlayStyle={{ position: 'fixed' }}
              align={{ targetOffset: [0, -46] }}
              overlay={
                <div className={style.moreOverlay}>
                  <StatedNavLink
                    to="/ranking"
                    initialState={CLEAR_BACK_STATE}
                    className={style.dropdownMenu}
                    onClick={() => handleClickMoreItem('Web3 Social Ranking')}>
                    <IconRanking className={style.dropdownMenuIcon} />
                    <span>{t('MorePage.ranking')}</span>
                  </StatedNavLink>
                  <a
                    href="https://official.debank.com/"
                    target="_blank"
                    rel="noreferrer"
                    className={style.dropdownMenu}
                    onClick={() => handleClickMoreItem('Official Account')}>
                    <IconOfficialConsole className={style.dropdownMenuIcon} />
                    <span>{t('OfficialAccounts.ynzhfc')}</span>
                  </a>
                  <StatedNavLink
                    to="/badge"
                    initialState={CLEAR_BACK_STATE}
                    className={style.dropdownMenu}
                    onClick={() => handleClickMoreItem('Web3 Badges')}>
                    <IconBadgeEntry className={style.dropdownMenuIcon} />
                    <span>{t('MorePage.web3-badges')}</span>
                  </StatedNavLink>
                  <StatedNavLink
                    to="/proposal"
                    initialState={CLEAR_BACK_STATE}
                    className={style.dropdownMenu}
                    onClick={() => handleClickMoreItem('Vote')}>
                    <IconVote className={style.dropdownMenuIcon} />
                    <span>{t('MorePage.vote')}</span>
                  </StatedNavLink>
                  <StatedNavLink
                    to="/groups"
                    initialState={CLEAR_BACK_STATE}
                    className={style.dropdownMenu}
                    onClick={() => handleClickMoreItem('Group')}>
                    <IconRcGroup className={style.dropdownMenuIcon} />
                    <span>{t('Sidebar.group')}</span>
                  </StatedNavLink>
                  <StatedNavLink
                    initialState={CLEAR_BACK_STATE}
                    to="/market"
                    className={style.dropdownMenu}
                    onClick={() => handleClickMoreItem('Market')}>
                    <IconRcMarket className={style.dropdownMenuIcon} />
                    <span className={style.menuText}>{t('Sidebar.market')}</span>
                  </StatedNavLink>
                  <StatedNavLink
                    to="/bundles"
                    initialState={CLEAR_BACK_STATE}
                    className={style.dropdownMenu}
                    onClick={() => handleClickMoreItem('Bundles')}>
                    <IconBundle className={style.dropdownMenuIcon} />
                    <span>{t('BundleList.vf79qp')}</span>
                  </StatedNavLink>
                  <StatedNavLink
                    to="/protocols"
                    initialState={CLEAR_BACK_STATE}
                    className={style.dropdownMenu}
                    onClick={() => handleClickMoreItem('Protocols')}>
                    <IconRcProtocols className={style.dropdownMenuIcon} />
                    <span>{t('Protocols.n8apq3')}</span>
                  </StatedNavLink>
                  <StatedNavLink
                    to="/paid"
                    initialState={CLEAR_BACK_STATE}
                    className={style.dropdownMenu}
                    onClick={() => handleClickMoreItem('Paid Features')}>
                    <IconRcPaid className={style.dropdownMenuIcon} />
                    <span>{t('Pro.u72fpt')}</span>
                  </StatedNavLink>
                  <a
                    href="https://cloud.debank.com/"
                    target="_blank"
                    rel="noreferrer"
                    className={style.dropdownMenu}
                    onClick={() => handleClickMoreItem('Portfolio API')}>
                    <IconRcPortfolio className={style.dropdownMenuIcon} />
                    <span>Portfolio API</span>
                  </a>
                  <StatedNavLink
                    to="/sybil"
                    initialState={CLEAR_BACK_STATE}
                    className={style.dropdownMenu}
                    onClick={() => handleClickMoreItem('Sybil')}>
                    <IconSybil className={style.dropdownMenuIcon} />
                    <span>{t('Sidebar.sybil')}</span>
                  </StatedNavLink>
                  <I18nSwitch>
                    <div
                      className={style.dropdownMenu}
                      onClick={() => handleClickMoreItem('Languages')}>
                      <IconRcI18n className={style.dropdownMenuIcon} />
                      <span>{t('Sidebar.1m930u3')}</span>
                    </div>
                  </I18nSwitch>
                  <StatedNavLink
                    to="/settings"
                    initialState={CLEAR_BACK_STATE}
                    className={style.dropdownMenu}
                    onClick={() => handleClickMoreItem('Settings')}>
                    <IconRcSettings className={style.dropdownMenuIcon} />
                    <span>{t('Sidebar.osmo8z')}</span>
                  </StatedNavLink>
                </div>
              }>
              <div className={style.navLink} onClick={handleClickMore}>
                <div className={style.menu}>
                  <IconMore className={style.menuIcon} />
                  <span className={style.menuText}>{t('Sidebar.1fj3p')}</span>
                </div>
              </div>
            </DropDown>
            <StreamOpenEditorButton
              className={style.streamCreateBtn}
              hideText={mini}
              onClick={handleClickNewPost}
            />
          </div>
        </div>
        <div className={style.bottom}>
          <AccountSwitcher mini={mini} />
        </div>
      </div>
    </nav>
  );
};
export default React.memo(Sidebar);
